import React, { useEffect, useState } from "react";
import api from "../api/api";
import { useNavigate } from "react-router-dom";

export default function QuickProfileSetup() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [profile, setProfile] = useState({
    fullName: "",
    phone: "",
    title: "",
    skills: "",
    avatar: null,
    resume: null,
  });

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [resumeName, setResumeName] = useState(null);

  useEffect(() => {
    // If user already has a profile, prefill (and optionally skip)
    (async () => {
      try {
        const res = await api.get("/profile");
        const p = res.data || {};
        setProfile((prev) => ({
          ...prev,
          fullName: p.fullName || "",
          phone: p.phone || "",
          title: p.title || "",
          skills: p.skills || "",
        }));
        if (p.avatar) setAvatarPreview(`/uploads/avatars/${p.avatar}`);
      } catch (e) {
        // 404 means no profile yet — that’s fine
      }
    })();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || files.length === 0) return;
    const file = files[0];
    setProfile((prev) => ({ ...prev, [name]: file }));
    if (name === "avatar") setAvatarPreview(URL.createObjectURL(file));
    if (name === "resume") setResumeName(file.name);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const fd = new FormData();
      fd.append("fullName", profile.fullName); // only required field
      fd.append("phone", profile.phone || "");
      fd.append("title", profile.title || "");
      fd.append("skills", profile.skills || "");
      if (profile.avatar instanceof File) fd.append("avatar", profile.avatar);
      if (profile.resume instanceof File) fd.append("resume", profile.resume);

      await api.put("/profile", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      navigate("/dashboard");
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-8 bg-white rounded-lg shadow mt-10">
      <h2 className="text-2xl font-bold text-center mb-2">Quick Profile Setup</h2>
      <p className="text-gray-600 text-center mb-6">
        Just the basics for now—you can complete the rest later.
      </p>

      {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <label className="block">
          <span className="text-gray-700 font-semibold">Full Name *</span>
          <input
            name="fullName"
            type="text"
            required
            value={profile.fullName}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </label>

        <label className="block">
          <span className="text-gray-700 font-semibold">Phone (required)</span>
          <input
            name="phone"
            type="tel"
            value={profile.phone}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </label>

        <label className="block">
          <span className="text-gray-700 font-semibold">Professional Title (required)</span>
          <input
            name="title"
            type="text"
            placeholder="e.g. Full Stack Developer"
            value={profile.title}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </label>

        <label className="block">
          <span className="text-gray-700 font-semibold">Skills (comma separated)</span>
          <input
            name="skills"
            type="text"
            placeholder="JavaScript, React, Node.js"
            value={profile.skills}
            onChange={handleChange}
            className="mt-1 block w-full rounded-md border px-3 py-2 focus:ring-2 focus:ring-indigo-500"
          />
        </label>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <label className="block">
            <span className="text-gray-700 font-semibold">Avatar (optional)</span>
            <input
              type="file"
              name="avatar"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full"
            />
            {avatarPreview && (
              <img
                src={avatarPreview}
                alt="Avatar Preview"
                className="mt-2 h-20 w-20 rounded-full object-cover border"
              />
            )}
          </label>

          <label className="block">
            <span className="text-gray-700 font-semibold">Resume (optional)</span>
            <input
              type="file"
              name="resume"
              accept=".pdf,.doc,.docx"
              onChange={handleFileChange}
              className="block w-full"
            />
            {resumeName && <p className="text-sm text-gray-600 mt-1">Selected: {resumeName}</p>}
          </label>
        </div>

        <button
  type="submit"
  disabled={
    saving ||
    !profile.fullName.trim() ||
    !profile.phone.trim() ||
    !profile.title.trim()
  }
  className="w-full py-3 text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-60"
>
  {saving ? "Saving..." : "Save & Continue"}
</button>
      </form>
    </div>
  );
}
